import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const CouponPopup = ({ onClose, forceShow = false }) => {
  const [isOpen, setIsOpen] = useState(true);
  const [timeLeft, setTimeLeft] = useState({
    hours: 23,
    minutes: 59,
    seconds: 59
  });

  // Close popup handler
  const handleClose = () => {
    setIsOpen(false);
    // Call the onClose prop if provided
    if (onClose) onClose();
  };

  // Countdown timer effect
  useEffect(() => {
    // If forceShow is true, always show the popup
    if (forceShow) {
      setIsOpen(true);
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else {
          clearInterval(timer);
          return { hours: 0, minutes: 0, seconds: 0 };
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [forceShow]);

  // Format time with leading zeros
  const formatTime = (time) => {
    return time < 10 ? `0${time}` : time;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center z-[10004] bg-black/50 backdrop-blur-sm p-3 sm:p-4 md:p-6">
      <div className="relative w-full max-w-[320px] sm:max-w-md lg:max-w-lg mx-auto bg-white/95 dark:bg-gray-800/95 backdrop-filter backdrop-blur-lg shadow-2xl border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden max-h-[85vh] sm:max-h-[90vh] overflow-y-auto">
        {/* Close button */}
        <button 
          onClick={handleClose}
          className="absolute top-2 right-2 sm:top-3 sm:right-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 z-10"
          aria-label="Close popup"
        >
          <X size={18} className="sm:w-5 sm:h-5" />
        </button>
        
        {/* Content Container */}
        <div className="p-4 sm:p-6 lg:p-8">
          {/* Logo */}
          <div className="flex justify-center mb-4 sm:mb-5">
            <div className="text-lg sm:text-xl lg:text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600">
              OSOP Coding
            </div>
          </div>
          
          {/* Content */}
          <div className="text-center">
            <h2 className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-800 dark:text-gray-100 mb-3 sm:mb-4">
              Special Offer
            </h2>
            
            <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 p-3 sm:p-4 lg:p-5 rounded-lg mb-4 sm:mb-5 lg:mb-6">
              <p className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 dark:text-white leading-tight">
                🔥 Use Code{' '}
                <span className="text-blue-600 dark:text-blue-400 font-mono text-base sm:text-lg lg:text-xl font-bold">
                  OSOP24
                </span>{' '}
                for 25% OFF
              </p>
              <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 mt-1 sm:mt-2">
                Valid for 24 Hours Only!
              </p>
            </div>
            
            {/* Timer */}
            <div className="mb-4 sm:mb-5 lg:mb-6">
              <div className="flex items-center justify-center space-x-1 sm:space-x-2 text-xs sm:text-sm lg:text-base font-semibold text-gray-800 dark:text-gray-200">
                <span className="bg-blue-600 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded text-xs sm:text-sm font-mono min-w-[2rem] sm:min-w-[2.5rem] text-center">
                  {formatTime(timeLeft.hours)}h
                </span>
                <span className="text-sm sm:text-base lg:text-lg">:</span>
                <span className="bg-blue-600 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded text-xs sm:text-sm font-mono min-w-[2rem] sm:min-w-[2.5rem] text-center">
                  {formatTime(timeLeft.minutes)}m
                </span>
                <span className="text-sm sm:text-base lg:text-lg">:</span>
                <span className="bg-blue-600 text-white px-2 py-1 sm:px-3 sm:py-1.5 rounded text-xs sm:text-sm font-mono min-w-[2rem] sm:min-w-[2.5rem] text-center">
                  {formatTime(timeLeft.seconds)}s
                </span>
                <span className="ml-1 sm:ml-2 text-xs sm:text-sm">left</span>
              </div>
            </div>
            
            {/* CTA Button */}
            <button 
              className="w-full py-3 sm:py-3.5 lg:py-4 px-4 sm:px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-medium rounded-lg transition-all duration-200 transform hover:scale-[1.02] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 text-sm sm:text-base shadow-lg hover:shadow-xl"
              onClick={() => {
                // Apply coupon code logic here
                handleClose();
              }}
            >
              Claim Your Discount
            </button>
            
            <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
              Applicable on all courses and bootcamps
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CouponPopup; 